/* eslint-env browser */
/**
 * Fabricore Theme — JavaScript
 * Dark mode, mobile menu, product gallery (arrows, thumbnails),
 * hover zoom, lightbox, variant selection (image sync, price,
 * SKU, stock, URL, unavailable options), product accordion,
 * sticky mobile add-to-cart, quantity controls, cart auto-submit,
 * share buttons, FAQ accordion, newsletter popup, address form toggle
 */

(function () {
  'use strict'

  /* ========================================
   * DARK MODE TOGGLE
   * ======================================== */
  function initDarkMode () {
    const html = document.documentElement
    const toggleBtns = document.querySelectorAll('[data-theme-toggle]')

    // State already set by inline script in <head>, just update icons
    updateToggleIcons()

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        html.classList.toggle('dark')
        localStorage.theme = html.classList.contains('dark') ? 'dark' : 'light'
        updateToggleIcons()
      })
    })

    function updateToggleIcons () {
      const isDark = html.classList.contains('dark')
      toggleBtns.forEach(function (btn) {
        const darkIcon = btn.querySelector('.icon-dark')
        const lightIcon = btn.querySelector('.icon-light')
        if (darkIcon && lightIcon) {
          darkIcon.style.display = isDark ? 'none' : 'block'
          lightIcon.style.display = isDark ? 'block' : 'none'
        }
      })
    }
  }

  /* ========================================
   * MOBILE MENU
   * ======================================== */
  function initMobileMenu () {
    const toggle = document.querySelector('[data-mobile-menu-toggle]')
    const menu = document.querySelector('[data-mobile-menu]')
    const overlay = document.querySelector('[data-mobile-menu-overlay]')

    if (!toggle || !menu) return

    toggle.addEventListener('click', function () {
      const isOpen = menu.classList.contains('is-open')
      menu.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', !isOpen)
      document.body.style.overflow = isOpen ? '' : 'hidden'
      if (overlay) overlay.classList.toggle('is-visible')

      // Focus trap: focus first focusable element when opening
      if (!isOpen) {
        const firstLink = menu.querySelector('a, button')
        if (firstLink) firstLink.focus()
      }
    })

    if (overlay) {
      overlay.addEventListener('click', function () {
        menu.classList.remove('is-open')
        toggle.setAttribute('aria-expanded', 'false')
        document.body.style.overflow = ''
        overlay.classList.remove('is-visible')
        toggle.focus()
      })
    }

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        menu.classList.remove('is-open')
        toggle.setAttribute('aria-expanded', 'false')
        document.body.style.overflow = ''
        if (overlay) overlay.classList.remove('is-visible')
        toggle.focus()
      }
    })
  }

  /* ========================================
   * PRODUCT IMAGE GALLERY
   * Thumbnails, prev/next arrows, current index tracking
   * ======================================== */
  // Shared gallery state so other modules can read/update the current index
  const galleryState = { currentIndex: 0, images: [] }

  function initProductGallery () {
    const mainImage = document.querySelector('[data-main-image]')
    const thumbnails = document.querySelectorAll('[data-thumbnail]')
    const prevBtn = document.querySelector('[data-gallery-prev]')
    const nextBtn = document.querySelector('[data-gallery-next]')

    if (!mainImage) return

    // Build image list from thumbnails (or single image fallback)
    if (thumbnails.length > 0) {
      thumbnails.forEach(function (thumb) {
        galleryState.images.push({
          src: thumb.getAttribute('data-full-src'),
          highRes: thumb.getAttribute('data-high-res-src'),
          alt: thumb.querySelector('img') ? thumb.querySelector('img').alt : ''
        })
      })
    } else {
      galleryState.images.push({
        src: mainImage.src,
        highRes: mainImage.getAttribute('data-high-res-src') || mainImage.src,
        alt: mainImage.alt
      })
    }

    // Navigate to a specific image index
    function goToImage (index) {
      if (galleryState.images.length === 0) return
      // Wrap around
      if (index < 0) index = galleryState.images.length - 1
      if (index >= galleryState.images.length) index = 0

      galleryState.currentIndex = index
      const img = galleryState.images[index]
      mainImage.src = img.src
      mainImage.alt = img.alt
      mainImage.setAttribute('data-high-res-src', img.highRes)

      // Update active thumbnail
      thumbnails.forEach(function (t) {
        t.classList.remove('is-active')
      })
      if (thumbnails[index]) {
        thumbnails[index].classList.add('is-active')
        // Scroll thumbnail into view if needed
        thumbnails[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }

      // Dispatch custom event indicating the image changed
      const event = new CustomEvent('fc:gallery:changed', {
        detail: { src: img.src, highRes: img.highRes, alt: img.alt }
      })
      document.dispatchEvent(event)
    }

    // Thumbnail click
    thumbnails.forEach(function (thumb, idx) {
      thumb.addEventListener('click', function () {
        goToImage(idx)
      })
    })

    // Prev/Next arrow buttons
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation()
        goToImage(galleryState.currentIndex - 1)
      })
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation()
        goToImage(galleryState.currentIndex + 1)
      })
    }

    // Expose goToImage for variant selection
    galleryState.goToImage = goToImage
  }

  /* ========================================
   * VARIANT SELECTION
   * Uses embedded variant JSON for full data.
   * Updates price, compare-at, discount badge,
   * SKU, stock, gallery image, URL, and
   * marks unavailable options.
   * ======================================== */
  function initVariantSelection () {
    const variantSelect = document.querySelector('select[name="id"]')
    if (!variantSelect) return

    // Parse variant JSON data
    const variantDataEl = document.getElementById('fc-variant-data')
    let variants = []
    if (variantDataEl) {
      try {
        variants = JSON.parse(variantDataEl.textContent)
      } catch (e) {
        /* ignore */
      }
    }
    if (variants.length === 0) return

    // Parse money format
    const moneyFormatEl = document.getElementById('fc-money-format')
    let moneyFormat = '{{amount}}'
    if (moneyFormatEl) {
      try {
        moneyFormat = JSON.parse(moneyFormatEl.textContent)
      } catch (e) {
        /* ignore */
      }
    }

    // DOM references for dynamic updates
    const priceEl = document.querySelector('[data-current-price]')
    const comparePriceEl = document.querySelector('[data-compare-price]')
    const discountBadgeEl = document.querySelector('[data-discount-badge]')
    const stockEl = document.querySelector('[data-stock-status]')
    const skuEl = document.querySelector('[data-product-sku]')
    const addBtn = document.querySelector(
      '[data-testid="product-add-to-cart"]'
    )
    const stickyPriceEl = document.querySelector('[data-sticky-price]')
    const selectOptions = variantSelect.querySelectorAll('option')

    // Get all option groups (color swatches + material buttons)
    const optionGroups = document.querySelectorAll(
      '.fc-product__options-group'
    )
    if (optionGroups.length === 0) return

    // Format price in cents to money string
    function formatMoney (cents) {
      const amount = (cents / 100).toFixed(0) // No decimals
      // Handle common Shopify money formats
      return moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', amount)
        .replace('{{amount_with_comma_separator}}', amount)
        .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, amount)
    }

    // Bind click events on option buttons
    optionGroups.forEach(function (group) {
      const swatches = group.querySelectorAll('.fc-product__swatch')
      swatches.forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          // Skip unavailable if clicked
          if (this.classList.contains('fc-product__swatch--unavailable')) {
            return
          }
          swatches.forEach(function (s) {
            s.classList.remove('is-active')
          })
          this.classList.add('is-active')
          updateSelectedVariant()
        })
      })

      const materialBtns = group.querySelectorAll('.fc-product__material-btn')
      materialBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (
            this.classList.contains('fc-product__material-btn--unavailable')
          ) {
            return
          }
          materialBtns.forEach(function (b) {
            b.classList.remove('is-active')
          })
          this.classList.add('is-active')
          updateSelectedVariant()
        })
      })
    })

    // Collect currently selected option values
    function getSelectedOptions () {
      const selected = []
      optionGroups.forEach(function (group) {
        const activeSwatch = group.querySelector(
          '.fc-product__swatch.is-active'
        )
        if (activeSwatch) {
          selected.push(activeSwatch.getAttribute('title'))
          return
        }
        const activeBtn = group.querySelector(
          '.fc-product__material-btn.is-active'
        )
        if (activeBtn) {
          selected.push(activeBtn.textContent.trim())
        }
      })
      return selected
    }

    // Find a variant matching the given options array
    function findVariant (selectedOptions) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i]
        let match = true
        for (let j = 0; j < selectedOptions.length; j++) {
          if (v.options[j] !== selectedOptions[j]) {
            match = false
            break
          }
        }
        if (match) return v
      }
      return null
    }

    // Update all dynamic elements when variant changes
    function updateSelectedVariant () {
      const selectedOptions = getSelectedOptions()
      if (selectedOptions.length === 0) return

      const variant = findVariant(selectedOptions)
      if (!variant) return

      // Update hidden select
      variantSelect.value = variant.id.toString()
      selectOptions.forEach(function (opt) {
        opt.selected = opt.value === variant.id.toString()
      })

      // Update price
      if (priceEl) priceEl.textContent = formatMoney(variant.price)
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price)

      // Update compare-at price and discount badge
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        if (comparePriceEl) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price)
          comparePriceEl.style.display = ''
        }
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) /
            variant.compare_at_price) *
            100
        )
        if (discountBadgeEl) {
          discountBadgeEl.textContent = '-' + pct + '%'
          discountBadgeEl.style.display = ''
        }
      } else {
        if (comparePriceEl) comparePriceEl.style.display = 'none'
        if (discountBadgeEl) discountBadgeEl.style.display = 'none'
      }

      // Update stock status
      if (stockEl) {
        if (variant.available) {
          stockEl.className = 'fc-product__stock fc-product__stock--in'
          stockEl.innerHTML =
            '<span class="material-icons sm">check_circle</span> En Stock'
        } else {
          stockEl.className = 'fc-product__stock fc-product__stock--out'
          stockEl.innerHTML =
            '<span class="material-icons sm">cancel</span> Agotado'
        }
      }

      // Update SKU
      if (skuEl) {
        if (variant.sku) {
          skuEl.textContent = 'SKU: ' + variant.sku
          skuEl.style.display = ''
        } else {
          skuEl.style.display = 'none'
        }
      }

      // Update add-to-cart button state
      if (addBtn) {
        addBtn.disabled = !variant.available
      }

      // Swap gallery image if variant has a featured image
      if (variant.featured_image && galleryState.goToImage) {
        // Find the matching thumbnail by src
        const targetSrc = variant.featured_image.src
        let found = false
        for (let k = 0; k < galleryState.images.length; k++) {
          if (galleryState.images[k].src === targetSrc) {
            galleryState.goToImage(k)
            found = true
            break
          }
        }
        // If not found in thumbnails, update main image directly
        if (!found) {
          const mainImage = document.querySelector('[data-main-image]')
          if (mainImage) {
            mainImage.src = variant.featured_image.src
            mainImage.alt = variant.featured_image.alt || ''
            mainImage.setAttribute(
              'data-high-res-src',
              variant.featured_image.high_res_src || variant.featured_image.src
            )
          }
        }
      }

      // Update URL with variant ID
      if (window.history && window.history.replaceState) {
        const url = window.location.pathname + '?variant=' + variant.id
        window.history.replaceState(null, '', url)
      }

      // Mark unavailable options in other groups
      updateUnavailableOptions(selectedOptions)
    }

    // Determine which options in each group are unavailable
    function updateUnavailableOptions (selectedOptions) {
      optionGroups.forEach(function (group, groupIdx) {
        const buttons = group.querySelectorAll(
          '.fc-product__swatch, .fc-product__material-btn'
        )
        buttons.forEach(function (btn) {
          const btnValue = btn.classList.contains('fc-product__swatch')
            ? btn.getAttribute('title')
            : btn.textContent.trim()

          // Build a test options array with this button's value in its group
          const testOptions = selectedOptions.slice()
          testOptions[groupIdx] = btnValue

          // Check if any variant exists with these options
          let hasAvailable = false
          for (let i = 0; i < variants.length; i++) {
            const v = variants[i]
            let match = true
            for (let j = 0; j < testOptions.length; j++) {
              if (v.options[j] !== testOptions[j]) {
                match = false
                break
              }
            }
            if (match && v.available) {
              hasAvailable = true
              break
            }
          }

          // Toggle unavailable class
          if (btn.classList.contains('fc-product__swatch')) {
            btn.classList.toggle(
              'fc-product__swatch--unavailable',
              !hasAvailable
            )
          } else {
            btn.classList.toggle(
              'fc-product__material-btn--unavailable',
              !hasAvailable
            )
          }
        })
      })
    }

    // On page load: check for ?variant= param and pre-select
    function initFromUrl () {
      const params = new URLSearchParams(window.location.search)
      const variantId = params.get('variant')
      if (!variantId) return

      // Find the variant in our data
      let targetVariant = null
      for (let i = 0; i < variants.length; i++) {
        if (variants[i].id.toString() === variantId) {
          targetVariant = variants[i]
          break
        }
      }
      if (!targetVariant) return

      // Pre-select the options for this variant
      optionGroups.forEach(function (group, idx) {
        const targetValue = targetVariant.options[idx]
        if (!targetValue) return

        // Try swatches
        const swatches = group.querySelectorAll('.fc-product__swatch')
        swatches.forEach(function (s) {
          s.classList.toggle(
            'is-active',
            s.getAttribute('title') === targetValue
          )
        })

        // Try material buttons
        const materialBtns = group.querySelectorAll(
          '.fc-product__material-btn'
        )
        materialBtns.forEach(function (b) {
          b.classList.toggle('is-active', b.textContent.trim() === targetValue)
        })
      })

      // Trigger the update
      updateSelectedVariant()
    }

    initFromUrl()
  }

  /* ========================================
   * QUANTITY INPUT
   * ======================================== */
  function initQuantityInputs () {
    document.querySelectorAll('[data-quantity]').forEach(function (wrapper) {
      const input = wrapper.querySelector('input[type="number"]')
      const minus = wrapper.querySelector('[data-quantity-minus]')
      const plus = wrapper.querySelector('[data-quantity-plus]')

      if (!input) return

      if (minus) {
        minus.addEventListener('click', function () {
          const val = parseInt(input.value, 10) || 1
          if (val > 1) {
            input.value = val - 1
            input.dispatchEvent(new Event('change'))
          }
        })
      }

      if (plus) {
        plus.addEventListener('click', function () {
          const val = parseInt(input.value, 10) || 1
          input.value = val + 1
          input.dispatchEvent(new Event('change'))
        })
      }
    })
  }

  /* ========================================
   * CART QUANTITY AUTO-SUBMIT
   * When quantity changes on the cart page,
   * debounce and auto-submit the form
   * ======================================== */
  function initCartAutoSubmit () {
    const cartForms = document.querySelectorAll(
      '.fc-cart__item form, form[action*="/cart/change"]'
    )
    if (cartForms.length === 0) return

    let debounceTimer = null

    cartForms.forEach(function (form) {
      const input = form.querySelector('input[type="number"]')
      if (!input) return

      input.addEventListener('change', function () {
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(function () {
          form.submit()
        }, 600)
      })
    })
  }

  /* ========================================
   * COLLECTION FILTERS (mobile toggle)
   * ======================================== */
  function initCollectionFilters () {
    const toggle = document.querySelector('[data-filters-toggle]')
    const sidebar = document.querySelector('[data-filters-sidebar]')

    if (!toggle || !sidebar) return

    toggle.addEventListener('click', function () {
      sidebar.classList.toggle('is-open')
      const isOpen = sidebar.classList.contains('is-open')
      toggle.setAttribute('aria-expanded', isOpen)
    })
  }

  /* ========================================
   * SHARE BUTTONS
   * Native Web Share API on mobile,
   * fallback links on desktop, copy-to-clipboard
   * ======================================== */
  function initShareButtons () {
    const shareContainer = document.querySelector('[data-share-buttons]')
    if (!shareContainer) return

    // Show native share button if Web Share API is available
    const nativeBtn = shareContainer.querySelector('[data-share-native]')
    const fallbackBtns = shareContainer.querySelectorAll(
      '[data-share-fallback]'
    )

    if (nativeBtn && navigator.share) {
      nativeBtn.style.display = 'inline-flex'
      // Hide fallback buttons on mobile when native share is available
      fallbackBtns.forEach(function (btn) {
        btn.style.display = 'none'
      })

      nativeBtn.addEventListener('click', function () {
        navigator
          .share({
            title: this.getAttribute('data-share-title'),
            text: this.getAttribute('data-share-text'),
            url: this.getAttribute('data-share-url')
          })
          .catch(function () {
            // User cancelled or error — show fallbacks
            nativeBtn.style.display = 'none'
            fallbackBtns.forEach(function (btn) {
              btn.style.display = 'inline-flex'
            })
          })
      })
    }

    // Copy link button
    const copyBtn = shareContainer.querySelector('[data-share-copy]')
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        const url = this.getAttribute('data-share-url')
        const tooltip = this.querySelector('[data-copy-tooltip]')

        navigator.clipboard
          .writeText(url)
          .then(function () {
            if (tooltip) {
              tooltip.classList.add('is-visible')
              setTimeout(function () {
                tooltip.classList.remove('is-visible')
              }, 2000)
            }
          })
          .catch(function () {
            // Fallback: create a temporary input
            const tempInput = document.createElement('input')
            tempInput.value = url
            document.body.appendChild(tempInput)
            tempInput.select()
            document.execCommand('copy')
            document.body.removeChild(tempInput)

            if (tooltip) {
              tooltip.classList.add('is-visible')
              setTimeout(function () {
                tooltip.classList.remove('is-visible')
              }, 2000)
            }
          })
      })
    }
  }

  /* ========================================
   * FAQ ACCORDION
   * Toggle Q&A pairs open/closed
   * ======================================== */
  function initFaqAccordion () {
    const items = document.querySelectorAll('[data-faq-item]')
    if (items.length === 0) return

    items.forEach(function (item) {
      const trigger = item.querySelector('[data-faq-trigger]')
      const content = item.querySelector('[data-faq-content]')
      const icon = item.querySelector('[data-faq-icon]')

      if (!trigger || !content) return

      trigger.addEventListener('click', function () {
        const isOpen = item.classList.contains('is-open')

        // Close all other items
        items.forEach(function (other) {
          other.classList.remove('is-open')
          const otherContent = other.querySelector('[data-faq-content]')
          const otherIcon = other.querySelector('[data-faq-icon]')
          if (otherContent) otherContent.style.maxHeight = null
          if (otherIcon) otherIcon.textContent = 'expand_more'
        })

        // Toggle current item
        if (!isOpen) {
          item.classList.add('is-open')
          content.style.maxHeight = content.scrollHeight + 'px'
          if (icon) icon.textContent = 'expand_less'
        }
      })
    })
  }

  /* ========================================
   * NEWSLETTER POPUP
   * Shows a popup after 10 seconds on first visit
   * Dismissable, remembers state for 7 days
   * ======================================== */
  function initNewsletterPopup () {
    const popup = document.querySelector('[data-newsletter-popup]')
    if (!popup) return

    // Check if already dismissed
    const dismissed = localStorage.getItem('fc_newsletter_dismissed')
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10)
      const sevenDays = 7 * 24 * 60 * 60 * 1000
      if (Date.now() - dismissedAt < sevenDays) return
    }

    // Show popup after 10 seconds
    setTimeout(function () {
      popup.classList.add('is-visible')
    }, 10000)

    // Close button
    const closeBtn = popup.querySelector('[data-newsletter-popup-close]')
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        popup.classList.remove('is-visible')
        localStorage.setItem('fc_newsletter_dismissed', Date.now().toString())
      })
    }

    // Close on overlay click
    popup.addEventListener('click', function (e) {
      if (e.target === popup) {
        popup.classList.remove('is-visible')
        localStorage.setItem('fc_newsletter_dismissed', Date.now().toString())
      }
    })

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-visible')) {
        popup.classList.remove('is-visible')
        localStorage.setItem('fc_newsletter_dismissed', Date.now().toString())
      }
    })
  }

  /* ========================================
   * IMAGE HOVER ZOOM (desktop only)
   * Shows a magnified overlay on mouse hover
   * ======================================== */
  function initImageHoverZoom () {
    // Only activate on devices with a pointer (not touch)
    if (!window.matchMedia('(hover: hover)').matches) return

    const container = document.querySelector(
      '.fc-product__main-image-container'
    )
    const overlay = document.querySelector('[data-zoom-overlay]')
    const mainImage = document.querySelector('[data-main-image]')

    if (!container || !overlay || !mainImage) return

    container.addEventListener('mouseenter', function () {
      const highResSrc =
        mainImage.getAttribute('data-high-res-src') || mainImage.src
      overlay.style.backgroundImage = 'url(' + highResSrc + ')'
      overlay.classList.add('is-active')
    })

    container.addEventListener('mousemove', function (e) {
      if (!overlay.classList.contains('is-active')) return
      const rect = container.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 100
      const y = ((e.clientY - rect.top) / rect.height) * 100
      overlay.style.backgroundPosition = x + '% ' + y + '%'
    })

    container.addEventListener('mouseleave', function () {
      overlay.classList.remove('is-active')
    })

    // Listen to gallery changes and update the zoom overlay if active
    document.addEventListener('fc:gallery:changed', function (e) {
      if (overlay.classList.contains('is-active')) {
        const highResSrc = e.detail.highRes || e.detail.src
        overlay.style.backgroundImage = 'url(' + highResSrc + ')'
      }
    })
  }

  /* ========================================
   * PRODUCT LIGHTBOX
   * Full-screen image overlay with navigation
   * ======================================== */
  function initProductLightbox () {
    const lightbox = document.querySelector('[data-lightbox]')
    const lightboxImage = document.querySelector('[data-lightbox-image]')
    const lightboxCounter = document.querySelector('[data-lightbox-counter]')
    const closeBtn = document.querySelector('[data-lightbox-close]')
    const prevBtn = document.querySelector('[data-lightbox-prev]')
    const nextBtn = document.querySelector('[data-lightbox-next]')
    const mainImageContainer = document.querySelector(
      '.fc-product__main-image-container'
    )

    if (!lightbox || !lightboxImage) return

    let currentIndex = 0

    // Show lightbox at a specific image index
    function openLightbox (index) {
      if (galleryState.images.length === 0) return
      currentIndex = index
      updateLightboxImage()
      lightbox.classList.add('is-open')
      document.body.style.overflow = 'hidden'
    }

    // Close lightbox
    function closeLightbox () {
      lightbox.classList.remove('is-open')
      document.body.style.overflow = ''
    }

    // Update the lightbox image and counter
    function updateLightboxImage () {
      if (currentIndex < 0) currentIndex = galleryState.images.length - 1
      if (currentIndex >= galleryState.images.length) currentIndex = 0

      const img = galleryState.images[currentIndex]
      lightboxImage.src = img.highRes || img.src
      lightboxImage.alt = img.alt

      if (lightboxCounter) {
        lightboxCounter.textContent =
          currentIndex + 1 + ' / ' + galleryState.images.length
      }
    }

    // Click on main image opens lightbox
    if (mainImageContainer) {
      mainImageContainer.addEventListener('click', function (e) {
        // Don't open if clicking arrows or zoom overlay
        if (
          e.target.closest('[data-gallery-prev]') ||
          e.target.closest('[data-gallery-next]')
        ) {
          return
        }
        openLightbox(galleryState.currentIndex)
      })
    }

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeLightbox()
      })
    }

    // Close on backdrop click
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox()
    })

    // Prev/Next buttons
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        currentIndex--
        updateLightboxImage()
      })
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        currentIndex++
        updateLightboxImage()
      })
    }

    // Keyboard navigation
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') {
        currentIndex--
        updateLightboxImage()
      }
      if (e.key === 'ArrowRight') {
        currentIndex++
        updateLightboxImage()
      }
    })

    // Touch swipe support
    let touchStartX = 0
    let touchStartY = 0

    lightbox.addEventListener(
      'touchstart',
      function (e) {
        touchStartX = e.changedTouches[0].screenX
        touchStartY = e.changedTouches[0].screenY
      },
      { passive: true }
    )

    lightbox.addEventListener(
      'touchend',
      function (e) {
        const deltaX = e.changedTouches[0].screenX - touchStartX
        const deltaY = e.changedTouches[0].screenY - touchStartY

        // Only trigger swipe if horizontal movement is dominant
        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX > 0) {
            currentIndex--
          } else {
            currentIndex++
          }
          updateLightboxImage()
        }
      },
      { passive: true }
    )
  }

  /* ========================================
   * PRODUCT ACCORDION
   * Toggle description open/closed
   * ======================================== */
  function initProductAccordion () {
    const accordions = document.querySelectorAll('.fc-product__accordion')
    if (accordions.length === 0) return

    accordions.forEach(function (accordion) {
      const trigger = accordion.querySelector('[data-accordion-trigger]')
      const content = accordion.querySelector('[data-accordion-content]')
      const icon = accordion.querySelector('[data-accordion-icon]')

      if (!trigger || !content) return

      // Set initial max-height if accordion starts open
      if (accordion.classList.contains('is-open')) {
        content.style.maxHeight = content.scrollHeight + 'px'
      }

      trigger.addEventListener('click', function () {
        const isOpen = accordion.classList.contains('is-open')

        if (isOpen) {
          // Close: animate to 0
          content.style.maxHeight = content.scrollHeight + 'px'
          // Force reflow
          content.offsetHeight // eslint-disable-line no-unused-expressions
          content.style.maxHeight = '0'
          accordion.classList.remove('is-open')
          if (icon) icon.textContent = 'expand_more'
        } else {
          // Open: animate to scrollHeight
          accordion.classList.add('is-open')
          content.style.maxHeight = content.scrollHeight + 'px'
          if (icon) icon.textContent = 'expand_less'
        }
      })
    })
  }

  /* ========================================
   * STICKY MOBILE ADD-TO-CART BAR
   * Shows when original add-to-cart scrolls
   * out of view (mobile only)
   * ======================================== */
  function initStickyAddToCart () {
    const stickyBar = document.querySelector('[data-sticky-bar]')
    const addRow = document.querySelector('.fc-product__add-row')
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]')
    const productForm = document.querySelector(
      '.fc-product__info form[action*="/cart/add"]'
    )

    if (!stickyBar || !addRow) return

    // Use IntersectionObserver to detect when add-to-cart row leaves viewport
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              stickyBar.classList.remove('is-visible')
            } else {
              stickyBar.classList.add('is-visible')
            }
          })
        },
        { threshold: 0 }
      )

      observer.observe(addRow)
    }

    // Click on sticky button submits the product form
    if (stickyBtn && productForm) {
      stickyBtn.addEventListener('click', function () {
        // If the add-to-cart button is disabled (out of stock), scroll to options
        const mainAddBtn = productForm.querySelector(
          '[data-testid="product-add-to-cart"]'
        )
        if (mainAddBtn && mainAddBtn.disabled) {
          addRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        productForm.requestSubmit()
      })
    }
  }

  /* ========================================
   * MULTI-COLOR SELECTION
   * For products tagged with multi-color:X
   * ======================================== */
  function initMultiColorSelection () {
    const selector = document.querySelector('[data-multi-color-selector]')
    if (!selector) return

    const limit = parseInt(selector.getAttribute('data-limit'), 10)
    const swatches = selector.querySelectorAll('.fc-product__swatch--multi')
    const countDisplay = selector.querySelector('[data-multi-color-count]')
    const addBtn = document.querySelector(
      '[data-testid="product-add-to-cart"]'
    )
    const selectedColors = []

    function updateInputs () {
      // Update hidden inputs for properties
      for (let i = 1; i <= limit; i++) {
        const input = selector.querySelector(
          '[data-multi-color-input="' + i + '"]'
        )
        if (input) {
          input.value = selectedColors[i - 1] || ''
        }
      }

      // Update count display
      if (countDisplay) countDisplay.textContent = selectedColors.length

      // Disable add to cart if not all colors are selected
      if (addBtn) {
        if (selectedColors.length < limit) {
          addBtn.disabled = true
          addBtn.innerHTML =
            '<span class="material-icons sm">warning</span> Elige ' +
            limit +
            ' colores'
        } else {
          // Re-enable based on variant availability

          // Note: Full variant check is handled in initVariantSelection,
          // but we override text here.
          addBtn.disabled = false
          addBtn.innerHTML =
            '<span class="material-icons sm">add_shopping_cart</span> Añadir al carrito'
        }
      }
    }

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        const color = this.getAttribute('data-color-name')
        const index = selectedColors.indexOf(color)

        if (index > -1) {
          // Deselect
          selectedColors.splice(index, 1)
          this.classList.remove('is-selected')
        } else {
          // Select if under limit
          if (selectedColors.length < limit) {
            selectedColors.push(color)
            this.classList.add('is-selected')
          }
        }

        // Update UI indices on all swatches
        swatches.forEach(function (s) {
          const sColor = s.getAttribute('data-color-name')
          const sIndex = selectedColors.indexOf(sColor)
          const indexBadge = s.querySelector('.fc-product__swatch-index')
          if (sIndex > -1) {
            s.classList.add('is-selected')
            if (indexBadge) indexBadge.textContent = sIndex + 1
          } else {
            s.classList.remove('is-selected')
          }
        })

        updateInputs()
      })
    })

    // Initial run
    updateInputs()
  }

  /* ========================================
   * ADDRESS FORM TOGGLE
   * Toggle new address form visibility
   * ======================================== */
  function initAddressFormToggle () {
    const toggleBtns = document.querySelectorAll('[data-toggle-address-form]')
    const form = document.querySelector('[data-address-form]')

    if (toggleBtns.length === 0 || !form) return

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        form.classList.toggle('is-open')
      })
    })
  }

  /* ========================================
   * INIT ALL
   * ======================================== */
  document.addEventListener('DOMContentLoaded', function () {
    initDarkMode()
    initMobileMenu()
    initProductGallery()
    initImageHoverZoom()
    initProductLightbox()
    initVariantSelection()
    initMultiColorSelection()
    initQuantityInputs()
    initProductAccordion()
    initStickyAddToCart()
    initCartAutoSubmit()
    initCollectionFilters()
    initShareButtons()
    initFaqAccordion()
    initNewsletterPopup()
    initAddressFormToggle()
  })
})()
